<template>
	<ext-dialog
		v-model="visible"
		:title="hasName ? $t('edit_attribute') : $t('add_attribute')"
		dialogType="medium"
		:primaryButtonText="$t('save')"
		@primaryButtonAction="save"
		@closed="close"
	>
		<template #body>
			<v-form v-if="attribute" ref="form" v-model="valid" lazy-validation>
				<v-row>
					<v-col cols="12">
						<localized-text-field
							v-model="attribute.name"
							:defaultLanguage="companyDefaultLanguage"
							:options="companyLanguageOptions"
							:rules="rules.name"
							context="attribute"
							:label="$t('name')"
						/>
					</v-col>
				</v-row>
				<v-row>
					<v-col cols="12">
						<v-select
							v-model="attribute.type"
							:items="typeOptions"
							item-text="label"
							item-value="value"
							:label="$t('type')"
							dense
							outlined
							append-icon="icon-angle-down"
							:menu-props="{ offsetY: true }"
						/>
					</v-col>
				</v-row>

				<!-- multi-select option editor (unchanged) -->
				<div v-if="attribute.type === 'multi_select'">
					<v-row class="align-center">
						<v-col cols="11">
							<localized-text-field
								v-model="newOption"
								:defaultLanguage="companyDefaultLanguage"
								:options="companyLanguageOptions"
								context="option"
								:label="$t('option')"
							/>
						</v-col>
						<v-col cols="1" class="pa-0 mb-2">
							<v-btn icon small color="primary" @click="addOption">
								<v-icon>icon-plus</v-icon>
							</v-btn>
						</v-col>
					</v-row>
					<v-row v-if="attribute.options.length">
						<v-col cols="12" class="d-flex flex-wrap">
							<div
								v-for="(o, i) in attribute.options"
								:key="'chip-' + i"
								class="position-relative ma-1"
								style="display: inline-block"
							>
								<v-chip small @click="editOption(i)">{{ o.value[$lang] || o.value._default }}</v-chip>
								<v-btn
									icon
									x-small
									color="red"
									class="position-absolute"
									style="top: -6px; right: -6px"
									@click.stop="removeOption(i)"
								>
									<v-icon x-small>icon-cross</v-icon>
								</v-btn>
							</div>
						</v-col>
					</v-row>
				</div>
			</v-form>
		</template>
	</ext-dialog>
</template>

<script>
import ExtDialog from "../../../partials/dialogs/ExtDialog.vue";
import LocalizedTextField from "../../../controls/LocalizedTextField.vue";
import uiMixin from "../../../../store/interfaces/ui.mixin";
import { mapGetters } from "vuex";
import remote from "../../../../data/remote";

export default {
	name: "AttributeDialog",
	components: { ExtDialog, LocalizedTextField },
	mixins: [uiMixin],
	props: { value: Boolean, attributeData: Object, productId: String },
	data() {
		return {
			visible: false,
			valid: false,
			attribute: null,
			newOption: {},
			typeOptions: [
				{ label: this.$t("multiple_select"), value: "multi_select" },
				{ label: this.$t("yes_no"), value: "boolean" },
				{ label: this.$t("text_input"), value: "text" },
				{ label: this.$t("date_input"), value: "date" },
			],
			rules: {
				name: [
					(v) => !!v || this.$t("$validation.required", { item: this.$t("name") }),
					(v) =>
						Object.values(v).every((s) => s.length <= 100) ||
						this.$t("$validation.max_length", { item: this.$t("name"), length: "100" }),
				],
			},
		};
	},
	computed: {
		...mapGetters(["companyDefaultLanguage", "companyLanguageOptions"]),
		hasName() {
			return !!(this.attributeData && this.attributeData._id);
		},
		$lang() {
			return this.$i18n.locale;
		},
	},
	watch: {
		value(v) {
			this.visible = v;
			if (v) this.initAttribute();
		},
		visible(v) {
			this.$emit("input", v);
		},
	},
	methods: {
		initLocalizedObj() {
			const o = { _default: "" };
			this.companyLanguageOptions.forEach((l) => (o[l] = ""));
			return o;
		},
		initAttribute() {
			if (this.hasName) this.attribute = JSON.parse(JSON.stringify(this.attributeData));
			else
				this.attribute = {
					productId: this.attributeData?.productId || this.productId,
					name: this.initLocalizedObj(),
					type: "multi_select",
					options: [],
				};
			this.companyLanguageOptions.forEach((l) => {
				if (!this.attribute.name[l]) this.attribute.name[l] = "";
			});
			if (!this.attribute.name._default) this.attribute.name._default = "";
			this.newOption = this.initLocalizedObj();
		},
		addOption() {
			const def = this.companyDefaultLanguage;
			if (!(this.newOption[def] || "").trim())
				return this.showAlertDialog({ message: this.$t("$validation.required", { item: this.$t("option") }) });
			if (!this.newOption._default.trim()) this.newOption._default = this.newOption[def];
			this.attribute.options.push({ value: { ...this.newOption } });
			this.newOption = this.initLocalizedObj();
		},
		editOption(i) {
			const opt = this.attribute.options.splice(i, 1)[0];
			this.newOption = JSON.parse(JSON.stringify(opt.value));
		},
		async removeOption(i) {
			const opt = this.attribute.options[i];
			try {
				const prod = await new Promise((resolve, reject) => {
					remote.products.find(this.productId, { onSuccess: resolve, onFail: reject });
				});
				const used = prod.variants.some((v) =>
					(v.attributes || []).some((a) => {
						if (a.attributeId !== this.attribute._id) return false;
						if (Array.isArray(a.value)) return a.value.includes(opt._id);
						return a.value === opt._id;
					})
				);
				if (used) return this.showAlertDialog({ message: this.$t("option_in_use") });
				this.attribute.options.splice(i, 1);
			} catch (e) {
				console.error(e);
			}
		},
		save() {
			if (this.attribute.type === "multi_select" && (this.newOption[this.companyDefaultLanguage] || "").trim())
				return this.showAlertDialog({ message: this.$t("please_click_add_option") });

			const def = this.companyDefaultLanguage;
			const defVal = (this.attribute.name[def] || "").trim();
			if (!defVal) return this.showAlertDialog({ message: this.$t("$validation.required", { item: this.$t("name") }) });
			if (!this.attribute.name._default.trim()) this.attribute.name._default = defVal;
			if (this.attribute.type === "multi_select" && !this.attribute.options.length)
				return this.showAlertDialog({ message: this.$t("$validation.required", { item: this.$t("option") }) });

			this.$refs.form.validate();
			if (!this.valid) return;

			const payload = { ...this.attribute, companyDefaultLanguage: this.companyDefaultLanguage };
			const cb = {
				onSuccess: (p) => {
					this.$emit("save", p);
					this.visible = false;
					this.showSnackBar({ message: this.$t("$message.saved", { item: this.$t("attribute") }) });
				},
				onFail: () => {},
			};

			this.attribute._id /* <-- the fix: use _id */
				? remote.products.updateAttribute({
						productId: this.productId,
						attribute: payload,
						onSuccess: cb.onSuccess,
						onFail: cb.onFail,
				  })
				: remote.products.addAttribute({
						productId: this.productId,
						attribute: payload,
						onSuccess: cb.onSuccess,
						onFail: cb.onFail,
				  });
		},
		close() {
			this.visible = false;
		},
	},
};
</script>
